/**
 * Marketing Site Performance Optimization Utilities
 * Sub-2-second global load time optimizations for Cloudflare Workers/Pages
 */

// Critical resource preloading for marketing pages
export const preloadCriticalResources = () => {
  if (typeof document === 'undefined') return;

  // IBM Plex fonts are now loaded locally via @ibm/plex package
  // No external font preloading needed - fonts are bundled with the application

  // Preload critical images
  const criticalImages = ['/images/hero-bg-optimized.webp', '/images/features-icon-sprite.webp', '/logo.svg'];

  criticalImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = src;
    link.as = 'image';
    document.head.appendChild(link);
  });

  // DNS prefetch for external resources (Google Fonts removed - using local fonts)
  const dnsPrefetchDomains = ['https://api.sigma08.com', 'https://analytics.sigma08.com'];

  dnsPrefetchDomains.forEach(href => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = href;
    document.head.appendChild(link);
  });
};

// Optimize images for different viewport sizes
export const getOptimizedImageUrl = (
  baseUrl: string,
  width: number,
  format: 'webp' | 'avif' | 'jpg' = 'webp',
  quality: number = 85
): string => {
  // Use Cloudflare Image Resizing for optimal performance
  const params = new URLSearchParams({
    width: width.toString(),
    format,
    quality: quality.toString(),
    fit: 'scale-down',
  });

  return `/cdn-cgi/image/${params.toString()}/${baseUrl.replace(/^\//, '')}`;
};

// Critical CSS inlining for above-the-fold content
export const inlineCriticalCSS = () => {
  if (typeof document === 'undefined') return;

  const criticalCSS = `
    .marketing-landing-page { opacity: 1; }
    .hero-section {
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      min-height: 80vh;
      display: flex;
      align-items: center;
    }
    .hero-headline {
      font-family: 'IBM Plex Sans', system-ui, sans-serif;
      font-size: clamp(2.5rem, 5vw, 4rem);
      font-weight: 700;
      line-height: 1.2;
      color: #8a3ffc;
      margin-bottom: 1.5rem;
    }
    .hero-subheadline {
      font-family: 'IBM Plex Sans', system-ui, sans-serif;
      font-size: clamp(1.125rem, 2.5vw, 1.375rem);
      line-height: 1.5;
      color: #525252;
      margin-bottom: 2rem;
      max-width: 600px;
    }
    .hero-buttons {
      display: flex;
      gap: 1rem;
      margin-bottom: 2rem;
    }
    .cds--btn--primary {
      background-color: #8a3ffc;
      border-color: #8a3ffc;
    }
    .trust-badges {
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;
    }
  `;

  const style = document.createElement('style');
  style.textContent = criticalCSS; // Use textContent instead of innerHTML for CSS to prevent XSS
  document.head.appendChild(style);
};

// Lazy loading optimization for non-critical content
export const setupIntersectionObserver = () => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

  const lazyImageObserver = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;

          // Load the actual image source
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }

          // Load srcset if available
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }

          // Add fade-in animation
          img.style.opacity = '0';
          img.style.transition = 'opacity 0.3s ease-in-out';

          img.onload = () => {
            img.style.opacity = '1';
          };

          lazyImageObserver.unobserve(img);
        }
      });
    },
    {
      rootMargin: '50px 0px',
      threshold: 0.01,
    }
  );

  // Observe all images with data-src attribute
  document.querySelectorAll('img[data-src]').forEach(img => {
    lazyImageObserver.observe(img);
  });

  // Lazy load non-critical sections
  const sectionObserver = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target;
          section.classList.add('section-loaded');
          sectionObserver.unobserve(section);
        }
      });
    },
    {
      rootMargin: '100px 0px',
      threshold: 0.1,
    }
  );

  document.querySelectorAll('.lazy-section').forEach(section => {
    sectionObserver.observe(section);
  });
};

// Performance monitoring for Core Web Vitals
export const initMarketingPerformanceMonitoring = () => {
  if (typeof window === 'undefined') return;

  // Mark critical rendering milestones
  performance.mark('marketing-start');

  // Listen for page load events
  window.addEventListener('load', () => {
    performance.mark('marketing-loaded');
    performance.measure('marketing-load-time', 'marketing-start', 'marketing-loaded');

    const loadTime = performance.getEntriesByName('marketing-load-time')[0]?.duration;
    if (loadTime) {
      // Track if we meet the <2s requirement
      const meetsPerformanceTarget = loadTime < 2000;

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'marketing_performance', {
          event_category: 'Performance',
          event_label: 'Page Load Time',
          value: Math.round(loadTime),
          custom_parameters: {
            meets_target: meetsPerformanceTarget,
            target_ms: 2000,
            actual_ms: Math.round(loadTime),
          },
        });
      }
    }
  });

  // Track LCP specifically for marketing pages
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver(list => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };

        if (lastEntry) {
          const lcp = lastEntry.startTime;
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'marketing_lcp', {
              event_category: 'Performance',
              event_label: 'Largest Contentful Paint',
              value: Math.round(lcp),
              custom_parameters: {
                meets_target: lcp < 2500, // LCP target is 2.5s
                target_ms: 2500,
                actual_ms: Math.round(lcp),
              },
            });
          }
        }
      });

      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (error) {
      console.warn('LCP monitoring failed:', error);
    }
  }
};

// Initialize all performance optimizations
export const initMarketingPerformanceOptimizations = () => {
  // Preload critical resources immediately
  preloadCriticalResources();

  // Inline critical CSS for above-the-fold content
  inlineCriticalCSS();

  // Set up performance monitoring
  initMarketingPerformanceMonitoring();

  // Set up lazy loading after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupIntersectionObserver);
  } else {
    setupIntersectionObserver();
  }
};

// Utility for measuring and reporting conversion funnel performance
export const trackConversionFunnelPerformance = (step: string, startTime: number) => {
  const endTime = performance.now();
  const duration = endTime - startTime;

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion_step_performance', {
      event_category: 'Marketing',
      event_label: `${step} Load Time`,
      value: Math.round(duration),
      custom_parameters: {
        step,
        duration_ms: Math.round(duration),
        meets_target: duration < 1000, // Each step should load in <1s
      },
    });
  }
};

export default {
  preloadCriticalResources,
  getOptimizedImageUrl,
  inlineCriticalCSS,
  setupIntersectionObserver,
  initMarketingPerformanceMonitoring,
  initMarketingPerformanceOptimizations,
  trackConversionFunnelPerformance,
};
